<script setup>
import {ref, onMounted, defineProps, computed} from 'vue'
import {notify} from '@kyvg/vue3-notification'
import {useUserStore} from '@/stores/UserStore'
import {useAuthenticationStore, useDefaultStore} from '@/stores'
import {storeToRefs} from 'pinia'
import {useIsRouteActive} from '@shared/composables/router/useIsRouteActive'
import {findFirstAvailableRoute} from '@shared/utils/router'
import {PERFORMANCE_REPORT_PLATFORMS} from '@shared/constants/performanceReportPlatforms'
import {accessRules} from '@/permissions/accessControl'
import {authenticationStore} from '@/main'
import SideBarDropdown from '@/components/side-bar/SideBarDropdown.vue'
import RestrictedAccessMark from '@/components/ui/badges/RestrictedAccessMark.vue'
import TeamIcon from '@shared-components/ui/icons/side-bar/TeamIcon.vue'
import DashboardIcon from '@shared-components/ui/icons/side-bar/DashboardIcon.vue'
import BalanceBarAlert from '@shared-components/ui/alerts/BalanceBarAlert.vue'
import {useRouter} from 'vue-router'

const auth = authenticationStore
const router = useRouter()

const {isPoint2WebEmployee, isAgencyClient, hasAccessToProformaInvoices} = storeToRefs(useAuthenticationStore())
const {getUserManager} = useUserStore()
const {totalRequestsCount, meta} = storeToRefs(useDefaultStore())

defineProps({
  fullHeight: {
    type: Boolean,
    default: true,
  },
})

// Refs
const isCollapsed = ref(Number(localStorage.getItem('is-sidebar-collapsed')) || false)
const managerLoading = ref(false)
const hasNoManager = ref(false)
const reportsDropdownRef = ref()
const requestsDropdownRef = ref()
const assetsDropdownRef = ref()
const managersInfo = ref([
  {
    full_name: '',
    position: '',
    photo_path: null,
    manager_type: '',
    email: '',
    skype: '',
    telegram: '',
  },
])

// Computed
const isOpenRequestsInRequestDropdown = computed(() => {
  return Boolean(
    meta.value?.open_requests_counters?.ad_account_request ||
    meta.value?.open_requests_counters?.ad_account_rename_request ||
    meta.value?.open_requests_counters?.ad_account_top_up_request ||
    meta.value?.open_requests_counters?.whitelist_request ||
    meta.value?.open_requests_counters?.share_permissions_request ||
    meta.value?.open_requests_counters?.other_request ||
    false,
  )
})
const tooltipCreativeStudio = computed(() => {
  if (!isCollapsed.value) {
    return null
  }

  return {
    content: 'Creative Studio <i class="fas fa-external-link-alt pl-15-px"></i>',
    html: true,
  }
})
const toolsRouteName = computed(() => {
  const routeRecord = findFirstAvailableRoute(accessRules, 'tools', auth, router)
  return routeRecord?.name || 'tools'
})
const requestsDropdown = computed(() => {
  const items = [
    {
      title: 'All',
      routeName: 'my-requests.list',
      counter: null,
      access: accessRules.hasAccessToMyRequests(auth),
    },
    {
      title: 'Ad Accounts',
      routeName: 'ad-account-requests.accounts.list',
      counter: meta.value?.open_requests_counters?.ad_account_request ?? null,
      access: accessRules.hasAccessToAdAccountRequests(auth),
    },
    {
      title: 'Rename Ad Account',
      routeName: 'ad-account-requests.rename.list',
      counter: meta.value?.open_requests_counters?.ad_account_rename_request ?? null,
      access: accessRules.hasAccessToAccountRenameRequests(auth),
    },
    {
      title: 'Funds',
      routeName: 'ad-account-requests.top-up.list',
      counter: meta.value?.open_requests_counters?.ad_account_top_up_request ?? null,
      access: accessRules.hasAccessToTopUpRequestsPage(auth),
    },
    {
      title: 'Whitelist Ad Account',
      routeName: 'whitelist',
      counter: meta.value?.open_requests_counters?.whitelist_request ?? null,
      access: accessRules.hasAccessToWhiteListRequestsPage(auth),
    },
    {
      title: 'Share Access',
      routeName: 'share-permissions',
      counter: meta.value?.open_requests_counters?.share_permissions_request ?? null,
      access: accessRules.hasAccessToSharePermissionRequestsPage(auth),
    },
    {
      title: 'FB Pages',
      routeName: 'ad-account-requests.facebook-pages-requests.list',
      counter: null,
      access: accessRules.hasAccessToFBPageRequestsPage(auth),
    },
    {
      title: 'Creative Moderation',
      routeName: 'creative-requests.list',
      counter: null,
      access: accessRules.hasAccessToCreativeModerationRequest(auth),
    },
    {
      title: 'Client Pre-Moderation',
      routeName: 'client-pre-moderation.list',
      counter: null,
      access: accessRules.hasAccessToClientPreModerationList(auth),
    },
    {
      title: 'Other',
      routeName: 'other-requests.list',
      counter: meta.value?.open_requests_counters?.other_request ?? null,
      access: accessRules.hasAccessToOtherRequestList(auth),
    },
  ]

  return {
    core: {
      parentRoute: 'ad-account-requests',
    },
    mainButton: {
      title: 'Requests',
      counter: totalRequestsCount.value,
    },
    items: items.filter(item => item.access),
  }
})
const reportsDropdown = computed(() => {
  const items = [
    {
      title: 'Main',
      routeName: 'reports.main',
      counter: null,
      access: accessRules.hasAccessToDashboard(auth),
    },
    {
      title: 'Spend',
      routeName: 'reports.spend',
      counter: null,
      access: accessRules.hasAccessToDashboard(auth),
    },
    {
      title: 'Financial Activity',
      routeName: 'reports.financial-activity',
      counter: null,
      access: accessRules.hasAccessToDashboard(auth),
    },
    ...PERFORMANCE_REPORT_PLATFORMS.map(platform => ({
      title: platform.label,
      routeName: 'reports.performance',
      params: {platform: platform.key},
      counter: null,
      access: accessRules.hasAccessToPerformanceReport(auth, {params: {platform: platform.key}}),
    })),
  ]

  return {
    core: {
      parentRoute: 'client-reports',
    },
    mainButton: {
      title: 'Reports',
      counter: null,
    },
    items: items.filter(i => i.access),
  }
})
const companiesDropdown = computed(() => {
  const items = [
    {
      title: 'Companies',
      routeName: 'companies.list',
      counter: null,
      access: accessRules.hasAccessToCompaniesPage(auth),
    },
    {
      title: 'Documents',
      routeName: 'company-documents.list',
      counter: null,
      access: accessRules.hasAccessToSupportTools(auth),
      audience: 'support',
    },
  ]

  return {
    core: {
      parentRoute: 'companies',
    },
    mainButton: {
      title: 'Companies',
      counter: null,
    },
    items: items.filter(item => item.access),
  }
})

const assetsDropdown = computed(() => {
  const items = [
    {
      title: 'Ad Accounts',
      routeName: 'assets.ad-accounts.list',
      counter: null,
      access: true,
    },
    {
      title: 'Ad Accounts Connections',
      routeName: 'assets.ad-account-connections.list',
      counter: null,
      access: accessRules.hasAccessToAccountConnections(auth),
    },
    {
      title: 'Business Managers',
      routeName: 'assets.business-managers.list',
      counter: null,
      access: accessRules.hasAccessToBusinessManagers(auth),
    },
    {
      title: 'Chinese License',
      routeName: 'assets.facebook-license.list',
      counter: null,
      access: accessRules.hasAccessToFacebookLicense(auth),
    },
    {
      title: 'FB Pages',
      routeName: 'assets.facebook-pages.list',
      counter: null,
      access: accessRules.hasAccessToFacebookPages(auth),
    },
    {
      title: 'Credit Cards',
      routeName: 'assets.credit-cards.list',
      counter: null,
      access: accessRules.hasAccessToCreditCards(auth),
    },
    {
      title: 'Ad Appeals',
      routeName: 'compliance-appeals.list',
      counter: null,
      access: accessRules.hasAccessToComplianceAppeals(auth),
    },
  ]

  return {
    core: {
      parentRoute: 'assets.list',
    },
    mainButton: {
      title: 'Ad Accounts & Pages',
      counter: null,
    },
    items: items.filter(i => i.access),
  }
})

// Methods
onMounted(() => {
  if (!isPoint2WebEmployee.value) {
    getManagerInfo()
  }
})

const getManagerInfo = async () => {
  managerLoading.value = true

  try {
    const managers = await getUserManager()

    if (!managers || managers.length === 0) {
      hasNoManager.value = true
    } else {
      managersInfo.value = managers
    }
  } catch (error) {
    hasNoManager.value = true

    notify({type: 'error', text: error.data.message})
  } finally {
    managerLoading.value = false
  }
}

const showManagerType = manager_type => {
  return manager_type === 'media_planner_social' ? 'Social' : 'Search'
}

const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value
  if (isCollapsed.value) {
    reportsDropdownRef.value?.closeDropdownMenu()
    assetsDropdownRef.value?.closeDropdownMenu()
    requestsDropdownRef.value?.closeDropdownMenu()
  }

  localStorage.setItem('is-sidebar-collapsed', isCollapsed.value ? 1 : 0)
}
</script>

<template>
  <div class="bg-white sidebar position-sticky top-0" :class="{'sidebar--collapsed': isCollapsed}">
    <div class="sidebar-scrollable__inner" :class="{'overflow-y-auto overflow-x-hidden': !isCollapsed}">
      <div class="sidebar-head custom" :class="[isCollapsed ? 'py-9 px-6' : 'pt-9 p-8']">
        <img
          v-if="isCollapsed"
          src="~@shared-images/sidebar/pb_logo_mini.png"
          alt="pointboard"
          class="max-w-35-px d-block"
        />
        <img v-else src="~@shared-images/sidebar/pb_logo_new.webp" alt="pointboard" class="max-w-170-px d-block" />
      </div>
      <div class="sidebar-body">
        <balance-bar-alert :is-mini="isCollapsed" />

        <ul class="nav nav-pills flex-column" id="menu">
          <li v-tooltip.right="isCollapsed ? 'Dashboard' : null">
            <div class="nav-item">
              <router-link
                :class="['nav-link', useIsRouteActive('home').value ? 'active' : '']"
                :to="{name: 'home'}"
                active-class="active"
              >
                <span class="nav-icon d-flex align-items-center">
                  <dashboard-icon />
                </span>
                <span class="nav-text">Dashboard</span>
              </router-link>
            </div>
          </li>
          <li>
            <side-bar-dropdown
              ref="reportsDropdownRef"
              id="reports-sidebar-dropdown"
              parent-route-name="client-reports"
              :data="reportsDropdown"
              :is-mini="isCollapsed"
            >
              <template #button-icon>
                <i class="fa-light fa-chart-line"></i>
              </template>
            </side-bar-dropdown>
          </li>
          <li v-if="accessRules.hasAccessToRequestsPage(auth)">
            <side-bar-dropdown
              ref="requestsDropdownRef"
              id="requests-sidebar-dropdown"
              parent-route-name="ad-account-requests"
              :data="requestsDropdown"
              :is-mini="isCollapsed"
              :is-counter="isOpenRequestsInRequestDropdown"
            >
              <template #button-icon>
                <i class="fa-light fa-files"></i>
              </template>
            </side-bar-dropdown>
          </li>
          <li
            v-if="accessRules.hasAccessToCreativeModerationList(auth)"
            v-tooltip.right="isCollapsed ? 'Creative Moderation' : null"
          >
            <div class="nav-item">
              <router-link
                :to="{name: 'creatives.list'}"
                :class="['nav-link justify-content-between', useIsRouteActive('creatives.list').value ? 'active' : '']"
                active-class="active"
              >
                <div class="d-flex align-items-center">
                  <span class="nav-icon">
                    <i class="fa-light fa-image"></i>
                  </span>
                  <span class="nav-text">Creative Moderation</span>
                </div>
                <div v-if="false" class="small-badge-light-border ml-2">Beta</div>
              </router-link>
            </div>
          </li>
          <li v-if="companiesDropdown.items.length">
            <side-bar-dropdown
              id="companies-sidebar-dropdown"
              parent-route-name="companies"
              :data="companiesDropdown"
              :is-mini="isCollapsed"
            >
              <template #button-icon>
                <i class="fa-light fa-sitemap"></i>
              </template>
            </side-bar-dropdown>
          </li>
          <li v-if="accessRules.hasAccessToAssetsPage(auth)">
            <side-bar-dropdown
              ref="assetsDropdownRef"
              id="ad-accounts-sidebar-dropdown"
              parent-route-name="assets.list"
              :data="assetsDropdown"
              :is-mini="isCollapsed"
            >
              <template #button-icon>
                <i class="fa-light fa-images-user"></i>
              </template>
            </side-bar-dropdown>
          </li>
          <li
            v-if="accessRules.hasAccessToFinancePage(auth)"
            v-tooltip.right="isCollapsed ? (isAgencyClient ? 'Financial Overview' : 'Finance') : null"
          >
            <div class="nav-item">
              <router-link
                :to="{
                  name: isAgencyClient
                    ? 'payments.daily_balance'
                    : hasAccessToProformaInvoices
                      ? 'proforma-invoices.list'
                      : 'payments.list',
                }"
                :class="['nav-link', useIsRouteActive('finance').value ? 'active' : '']"
                active-class="active"
              >
                <span class="nav-icon">
                  <i class="fa-light fa-coins"></i>
                </span>

                <span class="nav-text">{{ isAgencyClient ? 'Financial Overview' : 'Finance' }}</span>
              </router-link>
            </div>
          </li>
          <li v-if="accessRules.hasAccessToTracker(auth)" v-tooltip.right="isCollapsed ? 'Tracker' : null">
            <div class="nav-item">
              <router-link
                :to="{name: 'campaigns.list'}"
                :class="['nav-link', useIsRouteActive('tracker').value ? 'active' : '']"
                active-class="active"
              >
                <span class="nav-icon">
                  <i class="fa-light fa-chart-mixed-up-circle-currency"></i>
                </span>

                <span class="nav-text">Tracker</span>
              </router-link>
            </div>
          </li>
          <li v-if="accessRules.hasAccessToReports(auth)" v-tooltip.right="isCollapsed ? 'Reports' : null">
            <div class="nav-item">
              <router-link
                :to="{name: 'reports'}"
                active-class="active"
                :class="['nav-link', useIsRouteActive('reports').value ? 'active' : '']"
              >
                <span class="nav-icon">
                  <i class="fa-light fa-chart-pie"></i>
                </span>

                <span class="nav-text">Reports</span>
              </router-link>
            </div>
          </li>
          <li
            v-if="accessRules.hasAccessToClientsDashboard(auth)"
            v-tooltip.right="isCollapsed ? 'Weekly Clients Report' : null"
          >
            <div class="nav-item">
              <router-link
                :to="{name: 'clients-dashboard'}"
                active-class="active"
                :class="['nav-link', useIsRouteActive('clients-dashboard').value ? 'active' : '']"
              >
                <span class="nav-icon">
                  <i class="fa-light fa-calendar-circle-user"></i>
                </span>

                <span class="nav-text">Weekly Clients Report</span>
              </router-link>
            </div>
          </li>
          <li v-if="accessRules.hasAccessToTools(auth)" v-tooltip.right="isCollapsed ? 'Tools' : null">
            <div class="nav-item">
              <router-link
                :to="{name: toolsRouteName}"
                :class="['nav-link', useIsRouteActive('tools').value ? 'active' : '']"
                active-class="active"
              >
                <span class="nav-icon">
                  <i class="fa-light fa-screwdriver-wrench"></i>
                </span>

                <span class="nav-text">Tools</span>
              </router-link>
            </div>
          </li>
          <li v-if="accessRules.hasAccessToSupportTools(auth)" v-tooltip.right="isCollapsed ? 'Support Tools' : null">
            <div class="nav-item">
              <router-link
                :to="{name: 'support-tools'}"
                :class="['nav-link', useIsRouteActive('support-tools').value ? 'active' : '']"
                active-class="active"
              >
                <span class="nav-icon">
                  <i class="fa-light fa-toolbox"></i>
                </span>

                <span class="nav-text">Support Tools <restricted-access-mark audience="support" /></span>
              </router-link>
            </div>
          </li>
          <li v-if="accessRules.hasAccessToClientOnboarding(auth)" v-tooltip.right="isCollapsed ? 'Onboarding' : null">
            <div class="nav-item">
              <router-link
                :to="{name: 'onboarding'}"
                :class="['nav-link', useIsRouteActive('onboarding').value ? 'active' : '']"
                active-class="active"
              >
                <span class="nav-icon">
                  <i class="fa-light fa-user-plus"></i>
                </span>

                <span class="nav-text">Onboarding</span>
              </router-link>
            </div>
          </li>
          <li v-if="accessRules.hasAccessToClickupPage(auth)" v-tooltip.right="isCollapsed ? 'ClickUp' : null">
            <div class="nav-item">
              <router-link
                :to="{name: 'clickup.list'}"
                :class="['nav-link', useIsRouteActive('clickup.list').value ? 'active' : '']"
                active-class="active"
              >
                <span class="nav-icon">
                  <i class="fa-light fa-list-ul"></i>
                </span>

                <span class="nav-text">ClickUp</span>
              </router-link>
            </div>
          </li>
          <li v-if="accessRules.hasAccessToTeam(auth)" v-tooltip.right="isCollapsed ? 'Team' : null">
            <div class="nav-item">
              <router-link
                :to="{name: 'team'}"
                :class="['nav-link', useIsRouteActive('team').value ? 'active' : '']"
                active-class="active"
              >
                <span class="nav-icon">
                  <team-icon />
                </span>

                <span class="nav-text">Team</span>
              </router-link>
            </div>
          </li>
          <template v-if="!isPoint2WebEmployee">
            <li v-tooltip.right="tooltipCreativeStudio">
              <div class="nav-item">
                <a
                  href="https://point2web.com/creative-studio/"
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  class="nav-link justify-content-between"
                >
                  <div class="d-flex align-items-center">
                    <span class="nav-icon">
                      <i class="fa-light fa-lightbulb"></i>
                    </span>
                    <span class="nav-text">Creative Studio</span>
                  </div>
                  <span v-if="!isCollapsed"><i class="fas fa-external-link-alt pl-15-px"></i></span>
                </a>
              </div>
            </li>
          </template>
        </ul>

        <div class="pt-3" v-if="!isPoint2WebEmployee && !hasNoManager && !isCollapsed">
          <hr class="m-0 pb-3" />
          <div v-for="(manager, index) in managersInfo" :key="index" class="manager-info">
            <div class="px-4">
              <p class="fw-bold" v-if="index === 0">I'm here to help you:</p>
              <div class="d-flex gap-3 align-items-center">
                <div v-if="manager.photo_path" class="b-radius-50 w-80-px overflow-hidden border-2-grey">
                  <img :src="manager.photo_path" :alt="manager.full_name" class="d-block max-w-100 w-100" />
                </div>
                <div>
                  <p class="fw-bold mb-0">{{ manager.full_name }} ({{ showManagerType(manager.manager_type) }})</p>
                  <p class="mb-3">{{ manager.position }}</p>
                  <div class="d-flex gap-2 align-items-center">
                    <a
                      v-if="manager.skype"
                      :href="`skype:${manager.skype}?chat`"
                      rel="nofollow noopener noreferrer"
                      target="_blank"
                      class="d-block b-radius-50 h-30-px w-30-px"
                    >
                      <img
                        src="~@shared-images/sidebar/socials/teams.webp"
                        alt="skype"
                        class="d-block max-w-100 w-100"
                      />
                    </a>
                    <a
                      v-if="manager.telegram"
                      :href="manager.telegram"
                      rel="nofollow noopener noreferrer"
                      target="_blank"
                      class="d-block b-radius-50 h-30-px w-30-px"
                    >
                      <img
                        src="~@shared-images/sidebar/socials/telegram.webp"
                        alt="telegram"
                        class="d-block max-w-100 w-100"
                      />
                    </a>
                    <a
                      v-if="manager.email"
                      :href="`mailto:${manager.email}`"
                      rel="nofollow noopener noreferrer"
                      target="_blank"
                      class="d-block b-radius-50 h-30-px w-30-px"
                    >
                      <img
                        src="~@shared-images/sidebar/socials/email.webp"
                        alt="email"
                        class="d-block max-w-100 w-100"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <hr v-if="index + 1 < managersInfo.length" />
          </div>
        </div>
      </div>
    </div>
    <div class="sidebar__collapse-toggle" @click="toggleSidebar" v-tooltip.right="isCollapsed ? 'Open' : null">
      <img src="~@shared-images/sidebar/ui/collapse.svg" alt="collapse" class="d-block" />
      <span v-if="!isCollapsed">Close</span>
    </div>
  </div>
</template>
